import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import {
  MailClient, mailStatus, invalidateMailStatus, addrName, addrFull, fmtDate, fmtSize, bodyOf, sanitizeHtml,
} from '../../data/mailClient';
import Compose from './Compose';
import MailSettings from './MailSettings';
import './Mail.css';

// The inbox at /mail — three panes: folders + labels | thread list + search
// | thread reader. Routes: /mail, /mail/:mailboxId (folder), /mail/:mailboxId/:threadId.
// All data moves through data/mailClient.js → the backend's JMAP proxy; push
// updates arrive over the proxied JMAP EventSource.

const PAGE = 50;
const SIG_KEY = 'nventr.mail.signature';
const ROLE_ICON = { inbox: '▣', drafts: '✎', sent: '➚', archive: '▤', junk: '⊘', trash: '🗑' };

export default function Mail() {
  const toast = useToast();
  const navigate = useNavigate();
  const { mailboxId: routeFolder, threadId: routeThread } = useParams();

  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState('');
  const [client, setClient] = useState(null);
  const [openErr, setOpenErr] = useState('');
  const [folders, setFolders] = useState([]);
  const [list, setList] = useState({ list: [], total: null, ids: [] });
  const [listBusy, setListBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [thread, setThread] = useState(null);
  const [threadBusy, setThreadBusy] = useState(false);
  const [compose, setCompose] = useState(null); // null | { mode, email } | {}
  const [showSettings, setShowSettings] = useState(false);
  const [signature, setSignature] = useState(() => localStorage.getItem(SIG_KEY) || '');
  const [newLabel, setNewLabel] = useState('');
  const [mobilePane, setMobilePane] = useState('list'); // list | thread
  const pollRef = useRef(null);

  // ---- bootstrap: status → open mailbox → folders --------------------------------
  useEffect(() => {
    let alive = true;
    mailStatus().then((s) => alive && setStatus(s)).catch((e) => alive && setStatusErr(e.message));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!status?.enabled) return undefined;
    let alive = true;
    MailClient.open().then((c) => { if (alive) { setClient(c); setOpenErr(''); } }).catch((e) => alive && setOpenErr(e.message));
    return () => { alive = false; };
  }, [status?.enabled]);

  const loadFolders = useCallback(async (c = client) => {
    if (!c) return [];
    const f = await c.mailboxes();
    setFolders(f);
    return f;
  }, [client]);

  useEffect(() => { if (client) loadFolders(client).catch((e) => toast(e.message, 'danger')); }, [client, loadFolders, toast]);

  const byRole = useCallback((role) => folders.find((f) => f.role === role), [folders]);
  const inbox = byRole('inbox');
  const currentFolder = useMemo(() => folders.find((f) => f.id === routeFolder) || inbox || null, [folders, routeFolder, inbox]);

  // ---- list -----------------------------------------------------------------------
  const filter = useMemo(() => {
    if (query) return { text: query };
    return currentFolder ? { inMailbox: currentFolder.id } : {};
  }, [query, currentFolder]);

  const loadList = useCallback(async ({ append = false } = {}) => {
    if (!client || (!currentFolder && !query)) return;
    setListBusy(true);
    try {
      const r = await client.query({ filter, position: append ? list.list.length : 0, limit: PAGE });
      setList((prev) => (append ? { ...r, list: [...prev.list, ...r.list], ids: [...prev.ids, ...r.ids] } : r));
    } catch (e) {
      toast(e.message, 'danger');
    } finally {
      setListBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, filter, currentFolder, query]);

  useEffect(() => { setSelected(new Set()); loadList(); }, [loadList]);

  // ---- thread ---------------------------------------------------------------------
  useEffect(() => {
    if (!client || !routeThread) { setThread(null); return undefined; }
    let alive = true;
    setThreadBusy(true);
    client.thread(routeThread).then(async (emails) => {
      if (!alive) return;
      setThread({ id: routeThread, emails });
      setMobilePane('thread');
      const unread = emails.filter((e) => !e.keywords?.$seen).map((e) => e.id);
      if (unread.length) {
        await client.markSeen(unread).catch(() => {});
        setList((l) => ({ ...l, list: l.list.map((m) => (unread.includes(m.id) ? { ...m, keywords: { ...m.keywords, $seen: true } } : m)) }));
        loadFolders().catch(() => {});
      }
    }).catch((e) => alive && toast(e.message, 'danger')).finally(() => alive && setThreadBusy(false));
    return () => { alive = false; };
  }, [client, routeThread, loadFolders, toast]);

  // ---- push + fallback poll ---------------------------------------------------------
  useEffect(() => {
    if (!client) return undefined;
    const stop = client.events((types) => {
      if (types.includes('Mailbox') || types.includes('Email')) loadFolders().catch(() => {});
      if (types.includes('Email') || types.includes('Thread')) {
        loadList();
        if (routeThread) client.thread(routeThread).then((emails) => setThread({ id: routeThread, emails })).catch(() => {});
      }
    });
    pollRef.current = setInterval(() => { loadFolders().catch(() => {}); loadList(); }, 120000);
    return () => { stop(); clearInterval(pollRef.current); };
  }, [client, loadFolders, loadList, routeThread]);

  // ---- actions ----------------------------------------------------------------------
  const folderPath = (f) => `/mail/${f?.id || ''}`;
  const openThread = (m) => navigate(`/mail/${currentFolder?.id || inbox?.id || 'all'}/${m.threadId}`);
  const idsFor = (emailIds) => (emailIds?.length ? emailIds : [...selected]);
  const afterMutation = async (msg) => {
    setSelected(new Set());
    await Promise.all([loadList(), loadFolders()]);
    if (msg) toast(msg, 'ok');
  };
  const threadEmailIds = () => (thread?.emails || []).map((e) => e.id);

  const act = async (fn, msg) => {
    try { await fn(); await afterMutation(msg); } catch (e) { toast(e.message, 'danger'); }
  };
  const doArchive = (ids) => act(() => client.move(idsFor(ids), currentFolder?.id, (byRole('archive') || inbox).id), 'Archived');
  const doTrash = (ids) => {
    const trash = byRole('trash');
    const target = idsFor(ids);
    if (!trash || currentFolder?.id === trash.id) return act(() => client.destroy(target), 'Deleted forever');
    return act(() => client.move(target, currentFolder?.id, trash.id), 'Moved to trash');
  };
  const doJunk = (ids) => byRole('junk') && act(() => client.move(idsFor(ids), currentFolder?.id, byRole('junk').id), 'Marked as junk');
  const doSeen = (ids, seen) => act(() => client.markSeen(idsFor(ids), seen));
  const doFlag = (ids, on) => act(() => client.flag(idsFor(ids), on));
  const doLabel = (ids, labelId, on) => act(() => client.label(idsFor(ids), labelId, on), on ? 'Label added' : 'Label removed');
  const doMove = (ids, toId) => act(() => client.move(idsFor(ids), currentFolder?.id, toId), 'Moved');

  const addLabel = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    try { await client.createMailbox(newLabel.trim()); setNewLabel(''); await loadFolders(); toast('Label created', 'ok'); } catch (err) { toast(err.message, 'danger'); }
  };
  const removeLabel = async (f) => {
    if (!window.confirm(`Remove label "${f.name}"? Messages keep their other folders.`)) return;
    try { await client.destroyMailbox(f.id); await loadFolders(); if (routeFolder === f.id) navigate('/mail'); } catch (err) { toast(err.message, 'danger'); }
  };

  const onSignature = (v) => { setSignature(v); localStorage.setItem(SIG_KEY, v); };

  // Keyboard: j/k move, e archive, # trash, r reply, c compose, / search, u back.
  useEffect(() => {
    const onKey = (e) => {
      if (compose || showSettings) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.metaKey || e.ctrlKey) return;
      const idx = thread ? list.list.findIndex((m) => m.threadId === thread.id) : -1;
      if (e.key === 'c') { e.preventDefault(); setCompose({}); }
      else if (e.key === '/') { e.preventDefault(); document.getElementById('ml-search')?.focus(); }
      else if (e.key === 'j' && list.list[idx + 1]) openThread(list.list[idx + 1]);
      else if (e.key === 'k' && idx > 0) openThread(list.list[idx - 1]);
      else if (e.key === 'u' && thread) navigate(folderPath(currentFolder));
      else if (e.key === 'e' && thread) doArchive(threadEmailIds()).then(() => navigate(folderPath(currentFolder)));
      else if (e.key === '#' && thread) doTrash(threadEmailIds()).then(() => navigate(folderPath(currentFolder)));
      else if (e.key === 'r' && thread) setCompose({ mode: 'reply', email: thread.emails[thread.emails.length - 1] });
      else if (e.key === 'a' && thread) setCompose({ mode: 'replyAll', email: thread.emails[thread.emails.length - 1] });
      else if (e.key === 'f' && thread) setCompose({ mode: 'forward', email: thread.emails[thread.emails.length - 1] });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---- render --------------------------------------------------------------------------
  if (statusErr) return <div className="cs-page"><div className="cs-banner b-danger">{statusErr}</div></div>;
  if (!status) return <div className="cs-page"><div className="ml-loading"><div className="cs-skeleton" style={{ height: 32, width: 240 }} /><div className="cs-skeleton" style={{ height: 240 }} /></div></div>;
  if (!status.enabled) {
    return (
      <div className="cs-page">
        <div className="cs-page-head"><h1>Mail</h1></div>
        <div className="cs-empty">
          <p>Email is not turned on for this app yet.</p>
          <p className="cs-help">{status.canAdmin ? 'Turn it on from your app\u2019s page on nventr.io (Email section) — mailboxes on your domain, inbox right here.' : 'Ask an admin to enable the email service for this app.'}</p>
        </div>
      </div>
    );
  }
  if (openErr) return <div className="cs-page"><div className="cs-page-head"><h1>Mail</h1></div><div className="cs-banner b-danger">{openErr}</div></div>;
  if (!client) return <div className="cs-page"><div className="ml-loading"><div className="cs-skeleton" style={{ height: 32, width: 240 }} /><div className="cs-skeleton" style={{ height: 240 }} /></div></div>;

  const labels = folders.filter((f) => !f.role);
  const drafts = byRole('drafts');
  const isDraftsFolder = currentFolder?.role === 'drafts';
  const unreadInbox = inbox?.unreadEmails || 0;

  return (
    <div className={`ml ${mobilePane === 'thread' ? 'is-thread' : ''}`}>
      {/* ---- left: folders + labels ---- */}
      <aside className="ml-side" aria-label="Folders">
        <button type="button" className="cs-btn cs-btn-primary ml-compose-btn" onClick={() => setCompose({})}>Compose</button>
        <nav className="ml-folders">
          {folders.filter((f) => f.role).map((f) => (
            <Link key={f.id} to={folderPath(f)} className={`ml-folder ${currentFolder?.id === f.id && !query ? 'is-active' : ''}`} onClick={() => { setQuery(''); setSearch(''); setMobilePane('list'); }}>
              <span className="ml-folder-icon" aria-hidden>{ROLE_ICON[f.role] || '▫'}</span>
              <span className="ml-folder-name">{f.name}</span>
              {f.unreadEmails > 0 && f.role !== 'trash' && f.role !== 'junk' && <span className="ml-count">{f.unreadEmails}</span>}
              {f.role === 'drafts' && f.totalEmails > 0 && <span className="ml-count is-muted">{f.totalEmails}</span>}
            </Link>
          ))}
        </nav>
        <div className="ml-side-title">Labels</div>
        <nav className="ml-folders">
          {labels.map((f) => (
            <Link key={f.id} to={folderPath(f)} className={`ml-folder ${currentFolder?.id === f.id && !query ? 'is-active' : ''}`} onClick={() => { setQuery(''); setSearch(''); setMobilePane('list'); }}>
              <span className="ml-folder-icon ml-label-dot" aria-hidden>●</span>
              <span className="ml-folder-name">{f.name}</span>
              {f.unreadEmails > 0 && <span className="ml-count">{f.unreadEmails}</span>}
              <button type="button" className="ml-folder-x" aria-label={`Remove label ${f.name}`} onClick={(e) => { e.preventDefault(); removeLabel(f); }}>✕</button>
            </Link>
          ))}
          {!labels.length && <p className="cs-help ml-side-help">No labels yet.</p>}
        </nav>
        <form className="ml-newlabel" onSubmit={addLabel}>
          <input className="cs-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New label" aria-label="New label" />
          <button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!newLabel.trim()}>Add</button>
        </form>
        <div className="ml-side-foot">
          <div className="ml-me" title={client.address}>{client.address}</div>
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowSettings(true)}>Settings</button>
        </div>
        {status.paused && <div className="cs-banner b-warn ml-paused">Sending is paused{status.pauseReason ? `: ${status.pauseReason}` : ''}. Incoming mail still arrives.</div>}
      </aside>

      {/* ---- middle: list ---- */}
      <section className="ml-list" aria-label="Messages">
        <div className="ml-list-head">
          <form className="ml-search" onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); setMobilePane('list'); navigate('/mail'); }} role="search">
            <input id="ml-search" className="cs-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${client.address}`} aria-label="Search mail" />
            {query && <button type="button" className="ml-linkbtn" onClick={() => { setQuery(''); setSearch(''); }}>clear</button>}
          </form>
          <div className="ml-list-meta">
            <span className="cs-help">{query ? `Results for “${query}”` : currentFolder?.name}{list.total != null ? ` · ${list.total}` : ''}{unreadInbox && !query && currentFolder?.role === 'inbox' ? ` · ${unreadInbox} unread` : ''}</span>
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { loadList(); loadFolders(); }} disabled={listBusy} aria-label="Refresh">↻</button>
          </div>
          {selected.size > 0 && (
            <div className="ml-bulk" role="toolbar" aria-label="Selected messages">
              <span className="cs-help">{selected.size} selected</span>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doArchive()}>Archive</button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doTrash()}>Trash</button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doSeen(null, true)}>Read</button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doSeen(null, false)}>Unread</button>
              {byRole('junk') && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doJunk()}>Junk</button>}
              <LabelMenu labels={labels} folders={folders.filter((f) => f.role && f.id !== currentFolder?.id)} onLabel={(id) => doLabel(null, id, true)} onMove={(id) => doMove(null, id)} />
              <button type="button" className="ml-linkbtn" onClick={() => setSelected(new Set())}>clear</button>
            </div>
          )}
        </div>
        <div className="ml-rows">
          {listBusy && !list.list.length && [0, 1, 2, 3, 4].map((i) => <div key={i} className="cs-skeleton ml-row-skel" />)}
          {!listBusy && !list.list.length && <div className="cs-empty ml-empty">{query ? 'Nothing matches.' : currentFolder?.role === 'inbox' ? 'Inbox zero. New mail shows up here the moment it lands.' : 'Nothing here.'}</div>}
          {list.list.map((m) => {
            const unread = !m.keywords?.$seen;
            const active = thread?.id === m.threadId;
            const who = currentFolder?.role === 'sent' || currentFolder?.role === 'drafts' ? (m.to || []).map(addrName).join(', ') || '(no recipient)' : addrName((m.from || [])[0]) || '(unknown)';
            return (
              <div key={m.id} className={`ml-row ${unread ? 'is-unread' : ''} ${active ? 'is-active' : ''} ${selected.has(m.id) ? 'is-selected' : ''}`}>
                <input type="checkbox" className="ml-row-check" checked={selected.has(m.id)} aria-label="Select message" onChange={(e) => { const s = new Set(selected); e.target.checked ? s.add(m.id) : s.delete(m.id); setSelected(s); }} />
                <button type="button" className={`ml-star ${m.keywords?.$flagged ? 'is-on' : ''}`} aria-label={m.keywords?.$flagged ? 'Unflag' : 'Flag'} onClick={() => doFlag([m.id], !m.keywords?.$flagged)}>★</button>
                <button type="button" className="ml-row-main" onClick={() => (isDraftsFolder ? client.email(m.id).then((e) => setCompose({ mode: 'draft', email: e })) : openThread(m))}>
                  <span className="ml-row-top">
                    <span className="ml-row-who u-truncate">{who}</span>
                    <span className="ml-row-date">{m.hasAttachment && <span aria-label="has attachment" title="Attachment">📎 </span>}{fmtDate(m.receivedAt)}</span>
                  </span>
                  <span className="ml-row-subject u-truncate">{m.subject || '(no subject)'}</span>
                  <span className="ml-row-preview u-truncate">{m.preview}</span>
                </button>
              </div>
            );
          })}
          {list.total != null && list.list.length < list.total && (
            <button type="button" className="cs-btn cs-btn-ghost ml-more" onClick={() => loadList({ append: true })} disabled={listBusy}>{listBusy ? 'Loading…' : `Load more (${list.total - list.list.length} left)`}</button>
          )}
        </div>
      </section>

      {/* ---- right: thread ---- */}
      <section className="ml-thread" aria-label="Conversation">
        {!routeThread && <div className="ml-thread-empty cs-help">Select a conversation<br /><span className="ml-kbd-hint"><kbd className="cs-kbd">c</kbd> compose · <kbd className="cs-kbd">/</kbd> search · <kbd className="cs-kbd">j</kbd>/<kbd className="cs-kbd">k</kbd> next/prev · <kbd className="cs-kbd">e</kbd> archive · <kbd className="cs-kbd">r</kbd> reply</span></div>}
        {routeThread && threadBusy && !thread && <div className="ml-loading"><div className="cs-skeleton" style={{ height: 28, width: '60%' }} /><div className="cs-skeleton" style={{ height: 180 }} /></div>}
        {thread && (
          <>
            <div className="ml-thread-head">
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm ml-back" onClick={() => { setMobilePane('list'); navigate(folderPath(currentFolder)); }} aria-label="Back to list">←</button>
              <h2 className="ml-thread-subject">{thread.emails[0]?.subject || '(no subject)'}</h2>
              <div className="ml-thread-actions" role="toolbar" aria-label="Conversation actions">
                <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doArchive(threadEmailIds()).then(() => navigate(folderPath(currentFolder)))}>Archive</button>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doTrash(threadEmailIds()).then(() => navigate(folderPath(currentFolder)))}>Trash</button>
                {byRole('junk') && currentFolder?.role !== 'junk' && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doJunk(threadEmailIds()).then(() => navigate(folderPath(currentFolder)))}>Junk</button>}
                <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => doSeen(threadEmailIds(), false).then(() => navigate(folderPath(currentFolder)))}>Unread</button>
                <LabelMenu labels={labels} folders={folders.filter((f) => f.role && f.id !== currentFolder?.id)} current={thread.emails[thread.emails.length - 1]?.mailboxIds || {}} onLabel={(id, on) => doLabel(threadEmailIds(), id, on)} onMove={(id) => doMove(threadEmailIds(), id).then(() => navigate(folderPath(currentFolder)))} />
              </div>
            </div>
            <div className="ml-messages">
              {thread.emails.map((e, i) => (
                <Message key={e.id} email={e} client={client} expanded={i === thread.emails.length - 1 || thread.emails.length <= 3} isDraft={!!e.keywords?.$draft}
                  onReply={(mode) => setCompose({ mode, email: e })} onEditDraft={() => setCompose({ mode: 'draft', email: e })} onFlag={(on) => doFlag([e.id], on)} />
              ))}
            </div>
            <div className="ml-thread-foot">
              <button type="button" className="cs-btn cs-btn-outline" onClick={() => setCompose({ mode: 'reply', email: thread.emails[thread.emails.length - 1] })}>Reply</button>
              <button type="button" className="cs-btn cs-btn-ghost" onClick={() => setCompose({ mode: 'replyAll', email: thread.emails[thread.emails.length - 1] })}>Reply all</button>
              <button type="button" className="cs-btn cs-btn-ghost" onClick={() => setCompose({ mode: 'forward', email: thread.emails[thread.emails.length - 1] })}>Forward</button>
            </div>
          </>
        )}
      </section>

      {compose && (
        <Compose client={client} draftsId={drafts?.id} limits={status.limits} signature={signature} initial={compose} toast={(m) => toast(m, 'ok')}
          onClose={() => setCompose(null)} onSent={() => { toast('Sent', 'ok'); afterMutation(); }} />
      )}
      {showSettings && (
        <div className="cs-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowSettings(false); }}>
          <div className="cs-modal ml-settings-modal" role="dialog" aria-label="Mail settings">
            <MailSettings status={status} signature={signature} onSignature={onSignature} toast={(m) => toast(m, 'ok')} onClose={() => setShowSettings(false)}
              onChanged={() => { invalidateMailStatus(); mailStatus({ fresh: true }).then(setStatus).catch(() => {}); }} />
          </div>
        </div>
      )}
    </div>
  );
}

// Label/move dropdown (native select keeps it accessible + dependency-free).
function LabelMenu({ labels, folders, current = {}, onLabel, onMove }) {
  if (!labels.length && !folders.length) return null;
  return (
    <select className="cs-input ml-menu" value="" aria-label="Label or move" onChange={(e) => {
      const v = e.target.value;
      if (!v) return;
      if (v.startsWith('label:')) { const id = v.slice(6); onLabel(id, !current[id]); }
      if (v.startsWith('move:')) onMove(v.slice(5));
      e.target.value = '';
    }}>
      <option value="">Label / move…</option>
      {labels.length > 0 && <optgroup label="Labels">{labels.map((l) => <option key={l.id} value={`label:${l.id}`}>{current[l.id] ? '✓ ' : ''}{l.name}</option>)}</optgroup>}
      {folders.length > 0 && <optgroup label="Move to">{folders.map((f) => <option key={f.id} value={`move:${f.id}`}>{f.name}</option>)}</optgroup>}
    </select>
  );
}

// One message in the thread: collapsible header, sandboxed HTML (or
// preformatted text), attachments, remote-image gate.
function Message({ email, client, expanded: initialExpanded, isDraft, onReply, onEditDraft, onFlag }) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [remote, setRemote] = useState(false);
  const [cidMap, setCidMap] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const [attUrls, setAttUrls] = useState({});
  const { html, text } = useMemo(() => bodyOf(email), [email]);
  const attachments = (email.attachments || []).filter((a) => a.disposition !== 'inline' || !a.cid);
  const inline = (email.attachments || []).filter((a) => a.cid);
  const hasRemote = !!html && /\s(src|background)\s*=\s*["']?https?:/i.test(html);

  useEffect(() => {
    if (!expanded || !inline.length) return undefined;
    let alive = true;
    const urls = [];
    Promise.all(inline.map(async (a) => {
      try { const u = await client.blobObjectUrl(a.blobId, a.name || 'inline', a.type); urls.push(u); return [a.cid, u]; } catch { return null; }
    })).then((pairs) => alive && setCidMap(Object.fromEntries(pairs.filter(Boolean))));
    return () => { alive = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, email.id]);

  const openAttachment = async (a) => {
    try {
      const u = attUrls[a.blobId] || await client.blobObjectUrl(a.blobId, a.name, a.type);
      setAttUrls((m) => ({ ...m, [a.blobId]: u }));
      const link = document.createElement('a');
      link.href = u; link.download = a.name || 'attachment'; link.click();
    } catch (e) { /* toast lives in parent; keep quiet here */ }
  };

  const from = (email.from || [])[0];
  return (
    <article className={`ml-msg ${expanded ? 'is-open' : ''} ${isDraft ? 'is-draft' : ''}`}>
      <header className="ml-msg-head">
        <button type="button" className="ml-msg-toggle" onClick={() => setExpanded((x) => !x)} aria-expanded={expanded}>
          <span className="ml-avatar" aria-hidden>{(addrName(from) || '?').slice(0, 1).toUpperCase()}</span>
          <span className="ml-msg-who">
            <strong>{addrName(from) || '(unknown)'}</strong> <span className="cs-help">{from?.email}</span>
            {!expanded && <span className="ml-msg-preview cs-help"> — {email.preview}</span>}
          </span>
        </button>
        <span className="ml-msg-meta">
          {isDraft && <span className="cs-badge">draft</span>}
          <span className="cs-help" title={fmtDate(email.receivedAt, { long: true })}>{fmtDate(email.receivedAt)}</span>
          <button type="button" className={`ml-star ${email.keywords?.$flagged ? 'is-on' : ''}`} aria-label={email.keywords?.$flagged ? 'Unflag' : 'Flag'} onClick={() => onFlag(!email.keywords?.$flagged)}>★</button>
        </span>
      </header>
      {expanded && (
        <>
          <div className="ml-msg-to cs-help">
            to {(email.to || []).map(addrName).join(', ') || '—'}{email.cc?.length ? `, cc ${email.cc.map(addrName).join(', ')}` : ''}
            <button type="button" className="ml-linkbtn" onClick={() => setShowDetails((x) => !x)}>{showDetails ? 'hide details' : 'details'}</button>
          </div>
          {showDetails && (
            <dl className="ml-details">
              <dt>From</dt><dd>{(email.from || []).map(addrFull).join(', ')}</dd>
              <dt>To</dt><dd>{(email.to || []).map(addrFull).join(', ')}</dd>
              {email.cc?.length > 0 && <><dt>Cc</dt><dd>{email.cc.map(addrFull).join(', ')}</dd></>}
              {email.replyTo?.length > 0 && <><dt>Reply-To</dt><dd>{email.replyTo.map(addrFull).join(', ')}</dd></>}
              <dt>Date</dt><dd>{fmtDate(email.receivedAt, { long: true })}</dd>
              <dt>Message-ID</dt><dd className="ml-mono ml-break">{(email.messageId || []).join(' ')}</dd>
              <dt>Size</dt><dd>{fmtSize(email.size)}</dd>
            </dl>
          )}
          {hasRemote && !remote && (
            <div className="ml-remote-gate cs-help">Remote images are blocked. <button type="button" className="ml-linkbtn" onClick={() => setRemote(true)}>Load images</button></div>
          )}
          {html ? (
            <HtmlFrame html={sanitizeHtml(html, { remote, cidMap })} />
          ) : (
            <pre className="ml-text">{text}</pre>
          )}
          {attachments.length > 0 && (
            <ul className="ml-att-list">
              {attachments.map((a) => (
                <li key={a.blobId + (a.name || '')} className="ml-att">
                  <button type="button" className="ml-att-name ml-linkbtn" onClick={() => openAttachment(a)}>{a.name || 'attachment'}</button>
                  <span className="cs-help">{a.type} · {fmtSize(a.size)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="ml-msg-actions">
            {isDraft ? (
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={onEditDraft}>Edit draft</button>
            ) : (
              <>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onReply('reply')}>Reply</button>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onReply('replyAll')}>Reply all</button>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onReply('forward')}>Forward</button>
              </>
            )}
          </div>
        </>
      )}
    </article>
  );
}

// Sandboxed iframe sized to its content. No allow-scripts — nothing in the
// message can execute; allow-same-origin only so we can read scrollHeight
// (the CSP in sanitizeHtml still blocks every remote load until opted in).
function HtmlFrame({ html }) {
  const ref = useRef(null);
  const [h, setH] = useState(200);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onLoad = () => {
      try { setH(Math.min(Math.max(el.contentDocument?.documentElement?.scrollHeight || 200, 80), 20000)); } catch { /* cross-origin sandbox: keep default */ }
    };
    el.addEventListener('load', onLoad);
    return () => el.removeEventListener('load', onLoad);
  }, [html]);
  return <iframe ref={ref} className="ml-html" title="Message" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={html} style={{ height: h }} />;
}
